import { useTimeline } from '../../lib/queries-s2.js';
import { formatDateTime } from '../../lib/format.js';
import { Badge, EmptyState, ErrorState, LoadingState } from '../../components/ui/index.js';

const KIND_TONES = {
  lifecycle: 'accent',
  onboarding: 'info',
  task: 'neutral',
  milestone: 'neutral',
  note: 'neutral',
  case: 'warning',
  approval: 'info',
  operator_action: 'neutral',
} as const;

/** Aggregated, permission-filtered activity timeline. */
export function TimelineTab({ orgId }: { orgId: string }) {
  const query = useTimeline(orgId);
  if (query.isLoading) return <LoadingState />;
  if (query.isError || !query.data) {
    return (
      <ErrorState
        description="The timeline could not be loaded."
        onRetry={() => void query.refetch()}
      />
    );
  }
  if (query.data.items.length === 0) {
    return (
      <EmptyState title="No activity" description="State changes and communications appear here." />
    );
  }
  return (
    <ol className="space-y-0" aria-label="Activity timeline">
      {query.data.items.map((entry, i) => (
        <li key={entry.id} className="relative flex gap-3 pb-4 text-sm">
          <span className="flex flex-col items-center" aria-hidden="true">
            <span className="mt-1.5 size-2 rounded-full bg-accent-fg" />
            {i < query.data!.items.length - 1 && <span className="w-px flex-1 bg-border" />}
          </span>
          <div className="min-w-0 pb-1">
            <p className="flex flex-wrap items-center gap-2">
              <Badge tone={KIND_TONES[entry.kind]}>{entry.kind.replace('_', ' ')}</Badge>
              <span className="font-medium text-text-primary">{entry.title}</span>
            </p>
            {entry.detail && <p className="text-xs text-text-secondary">{entry.detail}</p>}
            <p className="text-xs text-text-muted">
              {entry.actorDisplayName ?? 'System'} · {formatDateTime(entry.occurredAt)}
            </p>
          </div>
        </li>
      ))}
    </ol>
  );
}
